import React, { useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { useDayforceWebhookDetailQuery } from 'queries/admin/dayforce/useDayforceWebhookDetailQuery'
import { useDayforceAgenciesContextValue } from '../../shared/context/DayforceAgenciesProvider'
import { DayforceAgency } from 'api/admin/dayforce/listDayforceAgencies'
import { Loading } from '@workwhile/ui'
import { useUpdateDayforceWebhookMutation } from 'queries/admin/dayforce/useUpdateDayforceWebhookMutation'
import { parseErrorMessage } from 'api'

type DayforceWebhookDetailContextValue = {
  state: {
    data: NonNullable<ReturnType<typeof useDayforceWebhookDetailQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: ReturnType<typeof useDayforceWebhookDetailQuery>['error'] | null
    agency: DayforceAgency | null
    archiveMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    refetch: () => void
    archive: () => void
  }
}

const [Context, useDayforceWebhookDetailContextValue] =
  createContext<DayforceWebhookDetailContextValue>({
    name: 'DayforceWebhookDetailContext',
  })

type DayforceWebhookDetailProviderProps = {
  children: React.ReactNode
  webhookId: number
}

export const DayforceWebhookDetailProvider = ({
  children,
  webhookId,
}: DayforceWebhookDetailProviderProps) => {
  const {
    data = null,
    dataUpdatedAt,
    isFetching,
    isError,
    error,
    refetch,
  } = useDayforceWebhookDetailQuery(webhookId)

  const {
    state: { agencies },
  } = useDayforceAgenciesContextValue()

  const {
    mutate: archiveWebhook,
    isPending: isArchivingWebhook,
    isError: isArchivingWebhookError,
    isSuccess: isArchivingWebhookSuccess,
    error: archiveWebhookError,
  } = useUpdateDayforceWebhookMutation()

  const agency = useMemo(() => {
    if (!data?.agencyId) {
      return null
    }
    return agencies[data.agencyId] ?? null
  }, [agencies, data?.agencyId])

  if (!data) {
    return <Loading />
  }

  const value: DayforceWebhookDetailContextValue = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: error ?? null,
      agency,
      archiveMutationState: {
        isPending: isArchivingWebhook,
        isError: isArchivingWebhookError,
        isSuccess: isArchivingWebhookSuccess,
        errorMessage: parseErrorMessage(archiveWebhookError),
      },
    },
    actions: {
      refetch: () => {
        void refetch()
      },
      archive: () => {
        archiveWebhook({
          webhookId: data.id,
          fromStatus: data.status,
          toStatus: 'completed',
        })
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useDayforceWebhookDetailContextValue }
